/**
 * Schedule First Step
 *
 * Redesigned scheduling step that prioritizes delivery timing and shows
 * real-time availability with improved UI consistency
 */

import { useFormContext } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { addDays, addMonths, format, parse, isPast, isToday } from 'date-fns';
import { useParams } from 'react-router-dom';
import { AvailabilityCalendar, type DayAvailability } from '../../../components/order/AvailabilityCalendar';
import { useProviderConfig } from '../../../contexts/ProviderConfigContext';
import { providerService } from '../../../services/provider.service';
import { Loader2Icon, AlertCircleIcon, CalendarIcon, TruckIcon } from 'lucide-react';
import type { OrderForm } from '../../../schemas/order.schema';

export function ScheduleFirstStep() {
  const { register, setValue, formState: { errors }, watch } = useFormContext<OrderForm>();
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const { config, isLoading: configLoading } = useProviderConfig();
  const [availability, setAvailability] = useState<Map<string, DayAvailability>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedDateStr = watch('deliveryDate');
  const selectedDate = selectedDateStr && typeof selectedDateStr === 'string' ? parse(selectedDateStr, 'yyyy-MM-dd', new Date()) : null;

  // Date boundaries - memoized to prevent infinite re-renders
  const minDate = useMemo(() => addDays(new Date(), 1), []); // Tomorrow
  const maxDate = useMemo(() => addMonths(new Date(), 3), []); // 3 months out

  // Fetch availability once config is loaded
  useEffect(() => {
    const fetchAvailability = async () => {
      // Wait for config to load and providerSlug to be available
      if (configLoading || !providerSlug) return;

      if (!config) {
        setError('Provider configuration not found');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await providerService.getAvailability(
          providerSlug,
          format(minDate, 'yyyy-MM-dd'),
          format(maxDate, 'yyyy-MM-dd')
        );

        // Convert API response to Map<string, DayAvailability>
        const availabilityMap = new Map<string, DayAvailability>();
        Object.entries(data.availability).forEach(([dateKey, dayData]) => {
          const date = parse(dateKey, 'yyyy-MM-dd', new Date());
          const status = dayData.status as 'available' | 'limited' | 'unavailable';
          availabilityMap.set(dateKey, {
            date,
            status,
            containers: dayData.containers,
            slots: status === 'available' ? 8 : status === 'limited' ? 3 : 0,
          });
        });

        setAvailability(availabilityMap);
      } catch (err) {
        setError('Unable to load availability. Please try again.');
        console.error('Failed to fetch availability:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [config, configLoading, providerSlug, minDate, maxDate]);

  const handleDateSelect = (date: Date, isASAP?: boolean) => {
    // Validate date is not in the past
    if (isPast(date) && !isToday(date)) {
      setError('Please select a future date');
      return;
    }

    // Format date as YYYY-MM-DD for the form
    const formattedDate = format(date, 'yyyy-MM-dd');
    setValue('deliveryDate', formattedDate, { shouldValidate: true });

    // Store ASAP flag if needed for order notes
    if (isASAP) {
      setValue('deliveryNotes', 'ASAP - Next available delivery requested');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section - Matching ServiceCatalog typography */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <CalendarIcon className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Schedule Delivery
          </h2>
        </div>
        <p className="text-lg text-gray-600 leading-relaxed">
          Select your preferred delivery date. We&apos;ll confirm availability and timing when we call.
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2Icon className="w-10 h-10 animate-spin text-primary-500 mb-4" />
            <p className="text-base text-gray-600">Checking availability...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Calendar */}
      {!isLoading && !error && (
        <>
          <div className={`bg-white rounded-lg border p-1 ${
            errors.deliveryDate ? 'border-red-500 border-2' : 'border-gray-200'
          }`}>
            <AvailabilityCalendar
              minDate={minDate}
              maxDate={maxDate}
              selectedDate={selectedDate}
              availability={availability}
              onDateSelect={handleDateSelect}
              showASAP={true}
              isLoading={isLoading}
            />
          </div>

          {/* Hidden input for form validation */}
          <input
            type="hidden"
            {...register('deliveryDate')}
            aria-required="true"
            aria-invalid={errors.deliveryDate ? "true" : "false"}
            aria-describedby={errors.deliveryDate ? "deliveryDate-error" : undefined}
          />

          {/* Enhanced error message display */}
          {errors.deliveryDate && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md" role="alert">
              <div className="flex items-start gap-3">
                <AlertCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 font-medium">
                  {errors.deliveryDate.message ? String(errors.deliveryDate.message) : 'Please select a valid delivery date'}
                </p>
              </div>
            </div>
          )}

          {/* Delivery Information Card */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TruckIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-blue-900 mb-2">
                  Delivery Information
                </p>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="block w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></span>
                    <span>Deliveries are typically made between 7 AM and 5 PM</span>
                  </li>
                  <li className="flex items-start">
                    <span className="block w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></span>
                    <span>We&apos;ll call you the day before to confirm timing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="block w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></span>
                    <span>Someone doesn&apos;t need to be present for delivery</span>
                  </li>
                  <li className="flex items-start">
                    <span className="block w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 mr-2 flex-shrink-0"></span>
                    <span>You can change your delivery date up to 24 hours before</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Selected Date Confirmation */}
          {selectedDate && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-green-600 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm font-medium text-green-800">
                  Delivery scheduled for: <span className="font-semibold">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}